import { useTranslation } from 'react-i18next';
import BrandLogo from '@/components/feature/BrandLogo';

export type AdminTab =
  | 'overview'
  | 'clients'
  | 'requests'
  | 'codes'
  | 'banks'
  | 'ips'
  | 'logs'
  | 'management'
  | 'settings';

type AdminSidebarProps = {
  activeTab: AdminTab;
  onSelect: (tab: AdminTab) => void;
};

type NavItem = {
  key: AdminTab;
  icon: string;
  labelKey: string;
};

const MENU_ITEMS: NavItem[] = [
  { key: 'overview', icon: 'ri-dashboard-3-line', labelKey: 'admin.nav.dashboard' },
  { key: 'clients', icon: 'ri-user-3-line', labelKey: 'admin.nav.clients' },
  { key: 'requests', icon: 'ri-file-transfer-line', labelKey: 'admin.nav.requests' },
  { key: 'codes', icon: 'ri-key-2-line', labelKey: 'admin.nav.codes' },
  { key: 'banks', icon: 'ri-bank-line', labelKey: 'admin.nav.banks' },
];

const SECURITY_ITEMS: NavItem[] = [
  { key: 'ips', icon: 'ri-global-line', labelKey: 'admin.nav.ips' },
  { key: 'management', icon: 'ri-delete-bin-6-line', labelKey: 'admin.nav.management' },
  { key: 'logs', icon: 'ri-file-list-3-line', labelKey: 'admin.nav.logs' },
  { key: 'settings', icon: 'ri-settings-3-line', labelKey: 'admin.nav.settings' },
];

export default function AdminSidebar({ activeTab, onSelect }: AdminSidebarProps) {
  const { t } = useTranslation();

  const renderItem = (item: NavItem) => {
    const active = activeTab === item.key;
    return (
      <button
        key={item.key}
        type="button"
        onClick={() => onSelect(item.key)}
        className={`flex w-full cursor-pointer items-center gap-3 whitespace-nowrap rounded-md px-3 py-2.5 text-sm font-semibold transition-colors ${
          active
            ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
            : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
        }`}
      >
        <span className="flex h-5 w-5 items-center justify-center">
          <i className={`${item.icon} text-base`}></i>
        </span>
        {t(item.labelKey)}
      </button>
    );
  };

  return (
    <aside className="flex h-full w-full flex-col gap-6 overflow-y-auto bg-background-50 px-3 py-5 lg:w-60">
      <a href="/admin" className="flex items-center gap-3 px-3">
        <BrandLogo className="h-14" />
      </a>

      <nav className="flex flex-col gap-1">
        <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-foreground-400">
          {t('admin.nav.section.menu')}
        </p>
        {MENU_ITEMS.map(renderItem)}
      </nav>

      <nav className="flex flex-col gap-1">
        <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-foreground-400">
          {t('admin.nav.section.security')}
        </p>
        {SECURITY_ITEMS.map(renderItem)}
      </nav>
    </aside>
  );
}